/**
 * Labels for icon-only controls.
 *
 * Every button in the tool rail already carries its label in a `title`
 * attribute, and that is worse than carrying none, because it reads as done.
 * A native tooltip waits about a second, renders in OS chrome that cannot be
 * styled, appears at the POINTER rather than at the control it describes — and
 * never fires on touch at all. So on a tablet the rail is a column of unlabelled
 * glyphs, and on a desktop it is a column of glyphs that will explain themselves
 * eventually if you hold still.
 *
 * Reported as "the mirror tool icons are confusing and users may not recognise
 * them", which was true, and true of the five icons that were there first.
 *
 * **Nothing is added to the markup.** The labels are read off `aria-label` and
 * `title`, and `title` is then removed so the native tooltip cannot also fire.
 * That is what lets this cover controls this file does not own — it is applied
 * to a container, not called per button. Anything that wants to override the
 * text or pin the tip to a particular side says so with a data attribute; see
 * `TIP ATTRIBUTES` below.
 *
 * Two lines where there are two things to say: the name, then what it does.
 * `aria-label` is the name and stays authoritative for screen readers; the tip
 * is decoration and is hidden from them, or the control gets announced twice.
 *
 * ## TIP ATTRIBUTES
 *
 * | Attribute        | Effect                                              |
 * |------------------|-----------------------------------------------------|
 * | `data-tip`       | Overrides the first line. Falls back to `aria-label` |
 * | `data-tip-what`  | Overrides the second line. Falls back to `title`     |
 * | `data-tip-place` | `end` \| `start` \| `above` \| `below` — side to try first |
 * | `data-tip-skip`  | Present: this control gets no tip at all            |
 *
 * ## STAYING PUT
 *
 * A tooltip that drifts off its control is worse than none, because it labels
 * whatever it happens to land on. Four things can cause it, and all four are
 * handled rather than hoped about:
 *
 *   1. **The anchor moves after the tip opens.** The rail grows when the
 *      mirroring controls mount, panes resize, `:active` scales a button by
 *      0.92. Measured once, the tip would sit where the control used to be — so
 *      it is re-measured every frame while visible.
 *   2. **The anchor disappears.** `unmountViewer()` removes two of these
 *      buttons. A tip left behind points at a gap.
 *   3. **The anchor disappears DURING the open delay.** `show` would then
 *      measure a detached element, get a zero box, and put the tip in the
 *      top-left corner of the window. This is the one that looks like a ghost.
 *   4. **Nowhere fits.** Handled in `tip-place.ts`, which clamps rather than
 *      letting the tip leave the viewport.
 */

import { anchorUsable, type Box, placeTip, type TipSide } from "./tip-place";

export interface TipOptions {
  /** Side to try first for every tip in this container. Default `end`. */
  placement?: TipSide;
  /** Milliseconds a pointer must rest before a tip appears. Default 250. */
  delay?: number;
  /** Gap between control and tip, in px. Default 10. */
  offset?: number;
  doc?: Document;
}

export interface Tips {
  /** Re-scan for controls added since mounting. */
  refresh(): void;
  destroy(): void;
}

const STYLE_ID = "tip-css";

const CSS = `
/*
  position: fixed and measured, rather than CSS anchor positioning.

  Anchor positioning would express "beside this, and flip when it would
  overflow" declaratively and is the better answer the moment it is everywhere.
  It is not everywhere yet, so it would need this measured path as a fallback
  regardless — and one code path that always runs is easier to trust than two
  where the rarely-taken one is the one that breaks.

  No transition on left/top. The tip is re-measured every frame while it is
  open, so an animated position would lag its own anchor by the length of the
  transition and read as the tip sliding around on its own.
*/
.tip {
  position: fixed;
  z-index: 200;
  max-inline-size: 15rem;
  padding: var(--space-2xs, 6px) var(--space-xs, 9px);
  border-radius: var(--radius-md, 10px);
  border: 1px solid var(--panel-border, light-dark(#e0e0e0, #2e2e2e));
  background: var(--surface-raised, light-dark(#fff, #1c1c1c));
  color: var(--text, light-dark(#111, #f2f2f2));
  font-family: var(--type-family-ui, system-ui, sans-serif);
  font-size: var(--type-size-xs, 0.75rem);
  line-height: 1.35;
  box-shadow: var(--shadow-md, 0 4px 14px rgb(0 0 0 / 0.22));
  pointer-events: none;
  opacity: 0;
  transition: opacity 90ms ease;
}
.tip[data-shown] { opacity: 1; }
.tip-name { font-weight: var(--type-weight-medium, 500); }
.tip-what { color: var(--text-muted, light-dark(#666, #9a9a9a)); }
.tip-name + .tip-what { margin-block-start: 1px; }
@media (prefers-reduced-motion: reduce) { .tip { transition: none; } }
`;

export interface TipLabels {
  name: string;
  /** The second line, or empty when there is nothing more to say. */
  what: string;
}

/**
 * The name, and what it does — or just the name when they say the same thing.
 *
 * Pure and exported because it is the only real decision in this file, and
 * because the two inputs are wildly inconsistent across the rail: Brush carries
 * "Brush" twice, Size carries two different strings, and Colour carries a hex
 * code and no title at all. Getting this wrong produces a tooltip that says
 * "Brush / Brush", which looks like a bug in a way a missing tooltip does not.
 */
export function tipLabels(ariaLabel: string, title: string): TipLabels | null {
  const name = ariaLabel.trim();
  const what = title.trim();
  if (!name && !what) return null;
  if (!name) return { name: what, what: "" };
  return { name, what: what.toLowerCase() === name.toLowerCase() ? "" : what };
}

const SIDES: TipSide[] = ["end", "start", "above", "below"];

/** The side this control asked for, or null to use the container's default. */
function placementOf(el: HTMLElement): TipSide | null {
  const want = (el.dataset.tipPlace ?? "").trim() as TipSide;
  return SIDES.includes(want) ? want : null;
}

/** Read a control's labels, taking its `title` over so it cannot fire natively. */
function labelsOf(el: HTMLElement): TipLabels | null {
  if (el.hasAttribute("data-tip-skip")) return null;
  // Taken over on first sight, and KEPT in a data attribute rather than
  // dropped, so `destroy()` can hand it back to a control this file does not
  // own.
  const title = (el.getAttribute("title") ?? "").trim();
  if (title) {
    if (!el.dataset.tipWhat) el.dataset.tipWhat = title;
    el.dataset.tipTitleTaken = "";
    el.removeAttribute("title");
  }
  return tipLabels(el.dataset.tip ?? el.getAttribute("aria-label") ?? "", el.dataset.tipWhat ?? "");
}

export function mountTips(root: HTMLElement, opts: TipOptions = {}): Tips {
  const doc = opts.doc ?? document;
  const delay = opts.delay ?? 250;
  const offset = opts.offset ?? 10;
  const defaultSide = opts.placement ?? "end";

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  const tip = doc.createElement("div");
  tip.className = "tip";
  // Decoration. The accessible name is on the control itself, and announcing
  // this as well would say everything twice.
  tip.setAttribute("aria-hidden", "true");
  tip.setAttribute("role", "presentation");
  doc.body.appendChild(tip);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let current: HTMLElement | null = null;
  let frame = 0;

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  function hide() {
    clearTimer();
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    current = null;
    tip.removeAttribute("data-shown");
  }

  /** Measure the anchor and put the tip beside it. False if it cannot be. */
  function pin(el: HTMLElement): boolean {
    // Detached, hidden or zero-sized. Measuring one of these gives a box at the
    // origin, and anchoring to that is how a tip ends up in the corner of the
    // window labelling nothing.
    if (!el.isConnected) return false;
    const r = el.getBoundingClientRect();
    const anchor: Box = { x: r.left, y: r.top, width: r.width, height: r.height };
    if (!anchorUsable(anchor)) return false;

    const box = tip.getBoundingClientRect();
    const placed = placeTip(
      anchor,
      { width: box.width, height: box.height },
      {
        width: doc.documentElement.clientWidth,
        height: doc.documentElement.clientHeight,
      },
      { preferred: placementOf(el) ?? defaultSide, offset },
    );
    tip.style.left = `${placed.left}px`;
    tip.style.top = `${placed.top}px`;
    tip.setAttribute("data-side", placed.side);
    return true;
  }

  /**
   * Re-pin every frame for as long as the tip is open.
   *
   * A one-off measurement is correct only until something moves, and plenty
   * does: the rail gains two buttons when the viewer mounts, `:active` scales a
   * button to 0.92 under the finger, panes resize. Re-measuring cannot drift by
   * construction, costs one rect read per frame, and only runs while a tip is
   * actually on screen — which is the rare case, not the common one.
   *
   * **Smoothness only.** Animation frames stop entirely while a page is hidden,
   * so anything that has to be TRUE rather than merely up to date cannot live
   * here. Measured in a background tab: zero callbacks in 400ms. The observer
   * below is what keeps the tip honest; this is what keeps it smooth.
   */
  function track(el: HTMLElement) {
    if (frame) cancelAnimationFrame(frame);
    const step = () => {
      if (current !== el || !pin(el)) {
        if (current === el) hide();
        return;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
  }

  /*
    The correctness half, and deliberately not on animation frames.

    Mutations are delivered as microtasks, so this still runs when the page is
    hidden and frames are not — which is exactly when a control can be removed
    without anything noticing. A tip whose anchor was deleted while the tab was
    in the background would otherwise still be there on return, labelling
    whatever moved into its place.

    Attributes as well as children, because the common way a control moves is
    somebody else's `style` or `class` changing, not its own removal.
  */
  const observer = new MutationObserver(() => {
    if (!current) return;
    if (!pin(current)) hide();
  });
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class", "hidden", "disabled"],
  });

  function show(el: HTMLElement) {
    const labels = labelsOf(el);
    if (!labels) return;

    tip.innerHTML = "";
    const name = doc.createElement("div");
    name.className = "tip-name";
    name.textContent = labels.name;
    tip.appendChild(name);
    if (labels.what) {
      const what = doc.createElement("div");
      what.className = "tip-what";
      what.textContent = labels.what;
      tip.appendChild(what);
    }

    // Positioned before it is revealed, so it never appears at the last tip's
    // location and slide into place.
    current = el;
    if (!pin(el)) {
      hide();
      return;
    }
    tip.setAttribute("data-shown", "");
    track(el);
  }

  /** The labelled control an event landed on, or null. */
  function controlFor(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    const el = target.closest<HTMLElement>("button, [role='button']");
    return el && root.contains(el) ? el : null;
  }

  const onOver = (e: PointerEvent) => {
    // Touch sends a pointerover just before the tap. Handled by pointerdown
    // instead, or the tip appears and is instantly dismissed by the click.
    if (e.pointerType === "touch") return;
    const el = controlFor(e.target);
    if (!el || el === current) return;
    clearTimer();
    // A delay, but a quarter of the native second. Long enough that sweeping
    // the pointer across the rail does not strobe, short enough to feel like an
    // answer rather than a wait.
    timer = setTimeout(() => {
      // Re-checked, because the wait is long enough for the control to have
      // been removed — the rail loses two buttons whenever the viewer
      // unmounts. Showing here would measure a detached element and strand the
      // tip in the corner of the window.
      if (el.isConnected) show(el);
    }, delay);
  };

  const onOut = (e: PointerEvent) => {
    const el = controlFor(e.target);
    if (!el) return;
    const to = e.relatedTarget;
    if (to instanceof Node && el.contains(to)) return;
    hide();
  };

  /*
    Touch has no hover, which is the half `title` cannot do at all.

    Shown on press and taken away on release, so the label arrives while the
    finger is still down and the button's own action fires as normal. No
    long-press to discover, and no tooltip that swallows the tap it was meant
    to explain.
  */
  const onDown = (e: PointerEvent) => {
    if (e.pointerType !== "touch") return;
    const el = controlFor(e.target);
    if (!el) return;
    clearTimer();
    show(el);
  };

  const onUp = (e: PointerEvent) => {
    if (e.pointerType === "touch") hide();
  };

  // Keyboard gets the same label, with no delay: someone tabbing has already
  // asked for it.
  const onFocus = (e: FocusEvent) => {
    const el = controlFor(e.target);
    if (!el) return;
    clearTimer();
    show(el);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") hide();
  };

  root.addEventListener("pointerover", onOver);
  root.addEventListener("pointerout", onOut);
  root.addEventListener("pointerdown", onDown);
  root.addEventListener("focusin", onFocus);
  root.addEventListener("focusout", hide);
  // A tip for a button that has just done something is stale by definition.
  root.addEventListener("click", hide);
  doc.addEventListener("keydown", onKey);
  doc.addEventListener("pointerup", onUp);
  /*
    Scroll and resize are handled by the per-frame re-pin, so these only exist
    to DISMISS. A tip that tracks its control perfectly while the page scrolls
    underneath is still a tip nobody asked to keep reading.
  */
  window.addEventListener("scroll", hide, true);
  window.addEventListener("resize", hide);

  /** Take over every `title` up front, so none of them can fire natively. */
  function refresh() {
    for (const el of root.querySelectorAll<HTMLElement>("button[title], [role='button'][title]")) {
      labelsOf(el);
    }
  }
  refresh();

  return {
    refresh,
    destroy() {
      hide();
      observer.disconnect();
      root.removeEventListener("pointerover", onOver);
      root.removeEventListener("pointerout", onOut);
      root.removeEventListener("pointerdown", onDown);
      root.removeEventListener("focusin", onFocus);
      root.removeEventListener("focusout", hide);
      root.removeEventListener("click", hide);
      doc.removeEventListener("keydown", onKey);
      doc.removeEventListener("pointerup", onUp);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      /*
        Hand back only the titles this actually took.

        A control whose second line came from `data-tip-what` in the markup
        never had a `title`, and inventing one on the way out would leave a
        native tooltip behind on a control that had none — the exact behaviour
        this file exists to remove.
      */
      for (const el of root.querySelectorAll<HTMLElement>("[data-tip-title-taken]")) {
        const what = el.dataset.tipWhat;
        if (what) el.setAttribute("title", what);
        delete el.dataset.tipWhat;
        delete el.dataset.tipTitleTaken;
      }
      tip.remove();
    },
  };
}
